import { useEffect, useState } from 'react';
import { MessageCircle, Users, Megaphone, SquarePen, X, Check, Lock } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Avatar from '../../lib/avatar';
import { SECTION_COLOR, SECTION_BG, hlavicky } from './komunikace';

/**
 * Nová konverzace — `ModalShell` (norma §1d: portál, nad lištou, na mobilu
 * zdola). Dřív vlastní `fixed` vrstva, která na desktopu visela u pravé hrany.
 *
 * Sdělení smí zakládat admin a kdo má podřízené (backend to hlídá stejně).
 */
export default function NovaKonverzace({ user, isDark, onClose, onVytvoreno }) {
    const [typ, setTyp] = useState('soukromy');
    const [nazev, setNazev] = useState('');
    const [prezdivka, setPrezdivka] = useState('');
    const [hledani, setHledani] = useState('');
    const [vysledky, setVysledky] = useState([]);
    const [vybrani, setVybrani] = useState([]);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);

    const smiSdeleni = user?.role === 'superadmin' || user?.role === 'admin' || !!user?.has_subordinates;
    const typy = [
        { id: 'soukromy', label: 'Soukromá', Icon: MessageCircle },
        { id: 'skupina', label: 'Skupina', Icon: Users },
        ...(smiSdeleni ? [{ id: 'sdeleni', label: 'Sdělení', Icon: Megaphone }] : []),
    ];

    useEffect(() => {
        if (!hledani.trim()) { setVysledky([]); return; }
        const t = setTimeout(() => {
            const kontext = typ === 'sdeleni' ? '&context=sdeleni' : '';
            fetch(`/api/chat/uzivatele?q=${encodeURIComponent(hledani)}${kontext}`)
                .then(r => (r.ok ? r.json() : [])).then(d => setVysledky(Array.isArray(d) ? d : [])).catch(() => setVysledky([]));
        }, 250);
        return () => clearTimeout(t);
    }, [hledani, typ]);

    const prepniTyp = (t) => { setTyp(t); setVybrani([]); setChyba(null); };
    const prepniCloveka = (u) => {
        if (typ === 'soukromy') { setVybrani([u]); return; }
        setVybrani(prev => (prev.some(p => p.id === u.id) ? prev.filter(p => p.id !== u.id) : [...prev, u]));
    };

    const lzeVytvorit = vybrani.length > 0 && (typ === 'soukromy' || nazev.trim()) && !ukladam;

    const vytvorit = async () => {
        if (!lzeVytvorit) return;
        setUkladam(true);
        setChyba(null);
        try {
            const res = await fetch('/api/chat/konverzace', {
                method: 'POST', headers: hlavicky(),
                body: JSON.stringify({
                    typ,
                    nazev: typ !== 'soukromy' ? nazev.trim() : null,
                    prezdivka: typ === 'sdeleni' ? (prezdivka.trim() || null) : null,
                    uzivatel_ids: vybrani.map(u => u.id),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(data.error || data.message || 'Konverzaci se nepodařilo založit.'); return; }
            onVytvoreno(data.id);
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
        } finally {
            setUkladam(false);
        }
    };

    const pole = {
        width: '100%', height: 40, borderRadius: 8, padding: '0 12px', outline: 'none', fontSize: 14,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)',
    };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '14px 0 6px' };

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="Nová konverzace" ikona={SquarePen} barva={SECTION_COLOR} sirka={480}
            paticka={(
                <div className="flex items-center gap-2">
                    {chyba && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
                    <button type="button" onClick={vytvorit} disabled={!lzeVytvorit} className="rounded-lg font-semibold ml-auto"
                        style={{ height: 42, padding: '0 18px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: lzeVytvorit ? 'pointer' : 'not-allowed', opacity: lzeVytvorit ? 1 : 0.45 }}>
                        {ukladam ? 'Zakládám…' : typ === 'soukromy' ? 'Napsat' : 'Založit'}
                    </button>
                </div>
            )}>
            <div style={{ padding: 16 }}>
                {/* Důvěrná linka na HR — výplata, docházka, smlouva */}
                <button type="button" className="w-full flex items-center text-left rounded-lg"
                    onClick={async () => {
                        setUkladam(true);
                        try {
                            const res = await fetch('/api/chat/linka-hr', { method: 'POST', headers: hlavicky(false) });
                            const d = await res.json().catch(() => ({}));
                            if (res.ok && d.id) onVytvoreno(d.id); else setChyba(d.error || 'Linku HR se nepodařilo otevřít.');
                        } finally { setUkladam(false); }
                    }}
                    style={{ gap: 12, minHeight: 56, padding: '8px 12px', marginBottom: 14, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, background: 'transparent', cursor: 'pointer' }}>
                    <span style={{ width: 34, height: 34, borderRadius: 9, background: SECTION_BG, color: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Lock size={16} /></span>
                    <span>
                        <span style={{ display: 'block', fontWeight: 700, color: 'var(--text-primary)' }}>Napsat HR — důvěrně</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>Výplata, docházka, smlouva. Vidí jen HR.</span>
                    </span>
                </button>
                <div className="flex gap-2 flex-wrap">
                    {typy.map(t => {
                        const on = typ === t.id;
                        return (
                            <button key={t.id} type="button" onClick={() => prepniTyp(t.id)} className="flex items-center gap-1.5 rounded-lg font-semibold"
                                style={{
                                    height: 36, padding: '0 12px', fontSize: 13, cursor: 'pointer',
                                    background: on ? SECTION_BG : 'transparent',
                                    border: `1px solid ${on ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                                    color: on ? SECTION_COLOR : 'var(--text-secondary)',
                                }}>
                                <t.Icon size={15} /> {t.label}
                            </button>
                        );
                    })}
                </div>
                {typ === 'sdeleni' && (
                    <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                        Sdělení čtou jen vaši podřízení a odpovídat se v něm nedá.
                    </p>
                )}

                {typ !== 'soukromy' && (
                    <>
                        <p style={popisek}>Název</p>
                        <input value={nazev} onChange={e => setNazev(e.target.value)} placeholder={typ === 'sdeleni' ? 'Např. Provoz Frenštát' : 'Např. Rozvozci'} style={pole} />
                    </>
                )}
                {typ === 'sdeleni' && (
                    <>
                        <p style={popisek}>Podpis autora (nepovinné)</p>
                        <input value={prezdivka} onChange={e => setPrezdivka(e.target.value)} placeholder="Např. Vedení" style={pole} />
                    </>
                )}

                <p style={popisek}>{typ === 'soukromy' ? 'Komu' : `Kdo · ${vybrani.length}`}</p>
                {vybrani.length > 0 && typ !== 'soukromy' && (
                    <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 8 }}>
                        {vybrani.map(u => (
                            <span key={u.id} className="flex items-center gap-1 rounded-full text-xs font-semibold"
                                style={{ padding: '3px 6px 3px 10px', background: SECTION_BG, color: SECTION_COLOR }}>
                                {u.jmeno}
                                <button type="button" onClick={() => prepniCloveka(u)} aria-label={'Odebrat ' + u.jmeno}
                                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex' }}><X size={13} /></button>
                            </span>
                        ))}
                    </div>
                )}
                <input value={hledani} onChange={e => setHledani(e.target.value)} placeholder="Hledat kolegu…" style={pole} autoFocus />
                <div style={{ marginTop: 6, maxHeight: 260, overflowY: 'auto' }}>
                    {vysledky.map(u => {
                        const on = vybrani.some(v => v.id === u.id);
                        return (
                            <button key={u.id} type="button" onClick={() => prepniCloveka(u)} className="w-full flex items-center text-left rounded-lg"
                                style={{ gap: 10, padding: '8px 10px', minHeight: 48, background: on ? SECTION_BG : 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}>
                                <Avatar url={u.avatar_url} jmeno={u.jmeno} size={30} isDark={isDark} />
                                <span className="flex-1 text-sm">{u.jmeno}</span>
                                {on && <Check size={16} style={{ color: SECTION_COLOR }} />}
                            </button>
                        );
                    })}
                    {hledani.trim() && vysledky.length === 0 && (
                        <p className="text-xs" style={{ color: 'var(--text-muted)', padding: '10px 4px' }}>Nikdo takový</p>
                    )}
                </div>
            </div>
        </ModalShell>
    );
}
